import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Channel } from '@/services/channelService';
import channelService from '@/services/channelService';
import { useSettingspageTranslations } from '@/hooks/useSettingspageTranslations';

interface ManageChannelsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (channel: Channel) => void;
}

export function ManageChannelsModal({ open, onClose, onSuccess }: ManageChannelsModalProps) {
  const [isCreating, setIsCreating] = useState(false);
  const queryClient = useQueryClient();
  const i18n = useSettingspageTranslations();

  const channelSchema = z.object({
    code: z
      .string()
      .min(2, 'Channel code must be at least 2 characters')
      .max(50, 'Channel code must be at most 50 characters'),
    name: z.string().min(1, 'Channel name is required'),
  });

  type ChannelFormValues = z.infer<typeof channelSchema>;

  const form = useForm<ChannelFormValues>({
    resolver: zodResolver(channelSchema),
    defaultValues: {
      code: '',
      name: '',
    },
  });

  // Fetch organization channels
  const {
    data: channels = [],
    isLoading,
    refetch,
  } = useQuery<Channel[]>({
    queryKey: ['channels'],
    queryFn: async () => {
      return await channelService.getChannels();
    },
    enabled: open,
  });

  // Create channel mutation
  const { mutate: createChannel, isPending: isCreatingChannel } = useMutation({
    mutationFn: async (data: ChannelFormValues) => {
      return await channelService.createChannel(data.code, data.name);
    },
    onSuccess: newChannel => {
      toast.success('Channel created successfully');
      form.reset();
      setIsCreating(false);
      refetch();
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['locales'] });
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      onSuccess?.(newChannel);
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.code?.[0] ||
        'Failed to create channel';
      toast.error(message);
    },
  });

  // Delete channel mutation
  const { mutate: deleteChannel } = useMutation({
    mutationFn: async (channelId: number) => {
      await channelService.deleteChannel(channelId);
    },
    onSuccess: () => {
      toast.success('Channel deleted successfully');
      refetch();
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ['channels'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      queryClient.invalidateQueries({ queryKey: ['locales'] });
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
      // Trigger parent component refetch
      onSuccess?.(null as any); // This will trigger useOrgSettings refetch
    },
    onError: (error: any) => {
      const message = error?.response?.data?.detail || 'Failed to delete channel';
      toast.error(message);
    },
  });

  const handleSubmit = (data: ChannelFormValues) => {
    createChannel(data);
  };

  const handleDelete = (channel: Channel) => {
    const confirmMessage = i18n.organization.manageChannels.deleteConfirm
      .replace('{{name}}', channel.name)
      .replace('{{code}}', channel.code);

    if (confirm(confirmMessage)) {
      deleteChannel(channel.id);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[80vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{i18n.organization.manageChannels.title}</DialogTitle>
          <DialogDescription>{i18n.organization.manageChannels.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Create Channel Section */}
          <div className="rounded-lg border p-4">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-medium">
                {i18n.organization.manageChannels.addNewTitle}
              </h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsCreating(!isCreating)}
              >
                <Plus className="mr-2 size-4" />
                {isCreating
                  ? i18n.organization.manageChannels.cancelButton
                  : i18n.organization.manageChannels.addChannelButton}
              </Button>
            </div>

            {isCreating && (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="code"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{i18n.organization.manageChannels.codeLabel}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder={i18n.organization.manageChannels.codePlaceholder}
                              maxLength={50}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{i18n.organization.manageChannels.nameLabel}</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder={i18n.organization.manageChannels.namePlaceholder}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" disabled={isCreatingChannel}>
                      {isCreatingChannel && <Loader2 className="mr-2 size-4 animate-spin" />}
                      {i18n.organization.manageChannels.createButton}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setIsCreating(false)}>
                      {i18n.organization.manageChannels.cancelButton}
                    </Button>
                  </div>
                </form>
              </Form>
            )}
          </div>

          {/* Channels List */}
          <div className="rounded-lg border">
            <div className="border-b p-4">
              <h3 className="text-lg font-medium">
                {i18n.organization.manageChannels.organizationChannelsTitle}
              </h3>
              <p className="text-sm text-muted-foreground">
                {i18n.organization.manageChannels.organizationChannelsDescription}
              </p>
            </div>

            {isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="mx-auto size-8 animate-spin" />
                <p className="mt-2 text-sm text-muted-foreground">
                  {i18n.organization.manageChannels.loadingMessage}
                </p>
              </div>
            ) : channels.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-muted-foreground">
                  {i18n.organization.manageChannels.noChannelsMessage}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{i18n.organization.manageLocales.codeHeader}</TableHead>
                    <TableHead>{i18n.organization.manageChannels.nameLabel}</TableHead>
                    <TableHead>{i18n.organization.manageLocales.statusHeader}</TableHead>
                    <TableHead className="text-right">
                      {i18n.organization.manageLocales.actionsHeader}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {channels.map(channel => (
                    <TableRow key={channel.id}>
                      <TableCell>
                        <Badge variant="outline">{channel.code}</Badge>
                      </TableCell>
                      <TableCell>{channel.name}</TableCell>
                      <TableCell>
                        <Badge variant={channel.is_active ? 'default' : 'secondary'}>
                          {channel.is_active
                            ? i18n.organization.manageLocales.activeStatus
                            : i18n.organization.manageLocales.inactiveStatus}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(channel)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {i18n.organization.manageChannels.closeButton}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
